import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { Chip, ModalCellItemIcon, ToggleCell } from "@molecules";
import { Icon, Textarea } from "@atoms";
import { FlexCol, FlexRow, H4, P, Subtitle } from "@quarks";
import { useAppSelector, useFormatDate, useRoomImage } from "@hooks";
import { getIsOnlineMeetingLinkSupported, MeetingRoom, RoomResource } from "@lib/store";
import { pickUsingStringAsIndex } from "@lib/utils";
import { pxToRem } from "@utils";
import { Caret, CellButton, StyledCell, StyledForm } from "./styles";
import { MeetingRoomBookingDetailsContentProps } from "./types";

const isResource = (room: MeetingRoom | RoomResource): room is RoomResource => "data" in room;

export const MeetingRoomBookingDetailsContent = ({
  attendees,
  dynamicTimeslot,
  isOnlineMeeting,
  message,
  onInviteesClick,
  onIsOnlineMeetingChange,
  onMessageChange,
  onTitleChange,
  room,
  title,
}: MeetingRoomBookingDetailsContentProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const onlineMeetingLinkSupported = useAppSelector(getIsOnlineMeetingLinkSupported);

  const displayName = isResource(room) ? room.name : room.displayName;
  const roomPictureHash = isResource(room) ? room.data.roomPictureHash ?? "" : room.roomPictureHash;
  const email = isResource(room) ? room.data.email ?? "" : room.email;
  const capacity = isResource(room) ? room.data.capacity : room.capacity;
  const { buildingId, buildingName, floorName } = room;

  const color = pickUsingStringAsIndex(email, theme.avatar.colors);
  const imageURL = useRoomImage(email || displayName, roomPictureHash, buildingId, 48);
  const subtitle = t("meeting:CreateMeetingRoomSummary", {
    count: capacity,
    floorName,
    buildingName,
  });

  const start = useFormatDate(dynamicTimeslot?.start?.toISOString() ?? "", "EEEE d MMMM HH:mm");
  const end = useFormatDate(dynamicTimeslot?.end?.toISOString() ?? "", "HH:mm");

  return (
    <FlexCol
      overflow="auto"
      flex="1">
      <StyledForm
        as="form"
        onSubmit={event => event.preventDefault()}>
        <StyledCell>
          <FlexCol gap={16}>
            <H4 as="h2">{t("hereAndNow:BookRoomNowSectionTitle")}</H4>
            <FlexRow
              justifyContent="flex-start"
              gap={16}>
              <ModalCellItemIcon
                imageURL={imageURL}
                backgroundColor={color}
                icon="room"
              />
              <FlexCol justifyContent="center">
                <P fontWeight="bold">{displayName}</P>
                <P>{subtitle}</P>
              </FlexCol>
            </FlexRow>
            <FlexRow
              justifyContent="center"
              alignSelf="flex-start"
              gap={8}>
              <Icon
                icon="clock"
                size={pxToRem(20)}
                color={theme.icon.inactive}
              />
              <Subtitle>
                {start} - {end}
              </Subtitle>
            </FlexRow>
          </FlexCol>
        </StyledCell>
        <FlexCol gap={2}>
          <StyledCell fontWeight="bold">
            <Textarea
              maxLength={255}
              name="title"
              onChange={event => onTitleChange(event.target.value)}
              placeholder={t("meeting:CreateMeetingTitlePlaceholder")}
              value={title}
            />
          </StyledCell>
          <StyledCell>
            <Textarea
              maxLength={600}
              name="message"
              onChange={event => onMessageChange(event.target.value)}
              placeholder={t("meeting:CreateMeetingMessagePlaceholder")}
              value={message}
            />
          </StyledCell>
        </FlexCol>
        <FlexCol gap={2}>
          <StyledCell>
            <CellButton
              as="button"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              onClick={onInviteesClick}>
              <H4 as="h2">{t("Invitees")}</H4>
              <Caret
                icon="caretRight"
                size={pxToRem(24)}
              />
            </CellButton>
          </StyledCell>
          {attendees.length > 0 && (
            <StyledCell>
              <FlexRow
                columnGap={8}
                justifyContent="flex-start"
                flexWrap="wrap"
                rowGap={4}>
                {attendees.map(user => (
                  <Chip
                    chip={user.id ? "colored" : "tertiary"}
                    email={user.email}
                    key={user.email}>
                    {user.name}
                  </Chip>
                ))}
              </FlexRow>
            </StyledCell>
          )}
        </FlexCol>
        {onlineMeetingLinkSupported && (
          <FlexCol gap={2}>
            <StyledCell>
              <H4 as="h2">{t("meeting:OnlineEvent")}</H4>
            </StyledCell>
            <StyledCell>
              <ToggleCell
                label={t("meeting:CreateMeetingOnlineMeetingToggleLabel")}
                checked={isOnlineMeeting}
                name="isOnlineMeeting"
                onToggle={event => onIsOnlineMeetingChange((event.target as HTMLInputElement).checked)}
              />
            </StyledCell>
          </FlexCol>
        )}
      </StyledForm>
    </FlexCol>
  );
};
